/**
 *
 * @export
 * @class BaseAPI
 */

import * as portableFetch from "portable-fetch";
import {Configuration} from "./configuration";
import {FetchAPI} from "./fetchApi";

const BASE_PATH = process.env.REACT_APP_BASE_PATH || "/openam";
const BASE_REALM = process.env.REACT_APP_BASE_REALM || "tpp";

export class BaseAPI {
    protected configuration: Configuration;

    constructor(configuration?: Configuration,
                protected basePath: string = BASE_PATH,
                protected fetch: FetchAPI = portableFetch,
                protected realm: string = BASE_REALM
    ) {
        if (configuration) {
            this.configuration = configuration;
            this.basePath = configuration.basePath || this.basePath;
            this.realm = configuration.realm || this.realm;
        }
    }
}

class UserRegistrationEmailInfo {
    mail: string;
}

export class UserConfirmedRegistrationInfo {
    input: UserRegistrationInfo;
    token: string
}

class UserRegistrationInfo {
    username: string;
    givenName: string;
    sn: string;
    userPassword: string;
    mail?: string;
    company?: string;
    cif?: string;
    telephoneNumber?: string;
}

class UserRegistrationConfirmInfo {
    token: string;
    code: string;
}

class PasswordResetConfirmInfo {
    token: string;
    code: string;
}

class PasswordChangeInfo {
    token: string;
    password: string;
}

class LoginInfo {
    username: string;
    password: string
}

interface AuthenticateData {
    realm?: string;
    successUrl?: string;
    tokenId?: string;
    authId?: string;
    callbacks?: any;
    header?: string;
    stage?: string;
    template?: string;
}

interface TypedResponse<T = any> extends Response {
    /**
     * this will override `json` method from `Body` that is extended by `Response`
     * interface Body {
     *     json(): Promise<any>;
     * }
     */
    json<P = T>(): Promise<P>
}

function getAuthRequest(loginInfo, authId) {
    const authRequestData = {
        "authId": authId,
        // "template": "",
        // "stage": "DataStore1",
        // "header": "Sign in",
        "callbacks": [
            {
                "_id":0,
                "type": "NameCallback",
                "output": [
                    {
                        "name": "prompt",
                        "value": "User Name:"
                    }
                ],
                "input": [
                    {
                        "name": "IDToken1",
                        "value": loginInfo.username
                    }
                ]
            },
            {
                "type": "PasswordCallback",
                "_id":1,
                "output": [
                    {
                        "name": "prompt",
                        "value": "Password:"
                    }
                ],
                "input": [
                    {
                        "name": "IDToken2",
                        "value": loginInfo.password
                    }
                ]
            }
        ]
    };
    return authRequestData;
}

class UserForgotPasswordInfo {
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
}

/**
 * ForgerockAmApi - functional programming interface
 * @export
 */
export class OpenamApi extends BaseAPI {

    public async getSessionInfo() {
        const {basePath} = this;
        const sessionUrl = `${basePath}/json/sessions?_action=getSessionInfo`;
        return fetch(sessionUrl, {
            "headers": {
                "accept": "application/json",
                "content-type": "application/json",
                "sec-fetch-dest": "empty",
                "sec-fetch-mode": "cors",
                "sec-fetch-site": "same-site",
                "x-requested-with": "XMLHttpRequest"
            },
            "method": "POST",
            "mode": "cors",
            "credentials": "include"
        });
    }

    public async authenticate(request: LoginInfo, authId: string): Promise<TypedResponse<AuthenticateData>> {
        const {basePath, realm} = this;
        const authenticateUrl = `${basePath}/json/realms/root/realms/${realm}/authenticate`;

        return fetch(authenticateUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
                , 'Accept-API-Version': 'protocol=1.0,resource=2.1'
            },
            credentials: 'include',
            body: JSON.stringify(getAuthRequest(request, authId)),
            method: 'POST',
        });
    }

    public async logout(token: string): Promise<any> {
        const {basePath} = this;
        const authenticateUrl = `${basePath}/json/sessions?_action=logout`;

        return fetch(authenticateUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest",
                "IPlanetDirectoryPro": token
            },
            credentials: 'include',
            method: 'POST',
        });
    }

    public async authenticateStart(): Promise<TypedResponse<AuthenticateData>> {
        const {basePath, realm} = this;
        const authenticateUrl = `${basePath}/json/realms/root/realms/${realm}/authenticate`;

        return fetch(authenticateUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
                , 'Accept-API-Version': 'protocol=1.0,resource=2.1'
            },
            credentials: 'include',
            method: 'POST',
        });
    }

    public async checkRegisterUserRequirements() {
        const {basePath, realm} = this;
        const registerUrl = `${basePath}/json/realms/root/realms/${realm}/selfservice/userRegistration`;

        return fetch(registerUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
            },
            credentials: 'include',
            method: 'GET',
        });
    }


    public async registerUserEmail(request: UserRegistrationEmailInfo, token?: string) {
        const {basePath, realm} = this;
        const registerUrl = `${basePath}/json/realms/root/realms/${realm}/selfservice/userRegistration?_action=submitRequirements`;

        return fetch(registerUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
            },
            body: JSON.stringify({
                token: token,
                input: request
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

    public async registerConfirmedUser(request: UserConfirmedRegistrationInfo) {
        const {basePath, realm} = this;
        const registerUrl = `${basePath}/json/realms/root/realms/${realm}/selfservice/userRegistration?_action=submitRequirements`;

        return fetch(registerUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
            },
            body: JSON.stringify({
                token: request.token,
                input: {
                    user: request.input
                }
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

    public async checkForgotPasswordRequirements() {
        const {basePath, realm} = this;
        const resetPasswordUrl = `${basePath}/json/realms/root/realms/${realm}/selfservice/forgottenPassword`;
        return fetch(resetPasswordUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
            },
            credentials: 'include',
            method: 'GET',
        });
    }

    public async sendRegisterCaptcha(token) {
        const {basePath, realm} = this;
        const resetPasswordUrl = `${basePath}/json/realms/root/realms/${realm}/selfservice/userRegistration?_action=submitRequirements`;
        return fetch(resetPasswordUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
            },
            credentials: 'include',
            body: JSON.stringify({
                input: {
                    response: token
                },
            }),
            method: 'POST',
        });
    }

    public async sendForgotPasswordCaptcha(token) {
        const {basePath, realm} = this;
        const resetPasswordUrl = `${basePath}/json/realms/root/realms/${realm}/selfservice/forgottenPassword?_action=submitRequirements`;
        return fetch(resetPasswordUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
               , 'Accept-API-Version': 'protocol=1.0,resource=2.1'
            },
            credentials: 'include',
            body: JSON.stringify({
                input: {
                    response: token
                },
            }),
            method: 'POST',
        });
    }


    public async forgotPassword(request: UserForgotPasswordInfo, token?: string) {
        const {basePath, realm} = this;
        const resetPasswordUrl = `${basePath}/json/realms/root/realms/${realm}/selfservice/forgottenPassword?_action=submitRequirements`;

        const tmp: string[] = [];

        if (request.username) {
            tmp.push(`uid eq "${request.username}"`);
        }
        if (request.email) {
            tmp.push(`mail eq "${request.email}"`);
        }
        if (request.firstName && request.lastName) {
            tmp.push(`(givenName eq "${request.firstName}" AND sn eq "${request.lastName}")`);
        } else if (request.firstName) {
            tmp.push(`givenName eq "${request.firstName}"`);
        } else if (request.lastName) {
            tmp.push(`sn eq "${request.lastName}"`);
        }

        let queryFilter = '(' + tmp.join(' OR ') + ')';

        return fetch(resetPasswordUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
            },
            body: JSON.stringify({
                token: token,
                input: {queryFilter: queryFilter}
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

    public async conformReset(request: PasswordResetConfirmInfo) {
        const {basePath, realm} = this;
        const resetPasswordUrl = `${basePath}/json/realms/root/realms/${realm}/selfservice/forgottenPassword?_action=submitRequirements`;

        return fetch(resetPasswordUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
            },
            body: JSON.stringify({
                token: request.token,
                input: {code: request.code}
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

    public async submitPassword(request: PasswordChangeInfo) {
        const {basePath, realm} = this;
        const resetPasswordUrl = `${basePath}/json/realms/root/realms/${realm}/selfservice/forgottenPassword?_action=submitRequirements`;

        return fetch(resetPasswordUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
            },
            body: JSON.stringify({
                token: request.token,
                input: {password: request.password}
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

    public async confirmUserRegistration(request: UserRegistrationConfirmInfo) {
        const {basePath, realm} = this;
        const registerUrl = `${basePath}/json/realms/root/realms/${realm}/selfservice/userRegistration?_action=submitRequirements`;

        return fetch(registerUrl, {
            headers: {
                "Content-Type": "application/json",
                "accept": "application/json",
                "X-Requested-With": " XMLHttpRequest"
            },
            body: JSON.stringify({
                token: request.token,
                input: {
                    code: request.code
                }
            }),
            credentials: 'include',
            method: 'POST',
        });
    }

}
